import React from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../Header';
import Sidebar from '../Sidebar';
import MobileNav from '../MobileNav';
import useMediaQuery from '../../hooks/useMediaQuery';
import { useAuth } from '../../contexts/AuthContext';

interface BaseLayoutProps {
  children: React.ReactNode;
}

const BaseLayout: React.FC<BaseLayoutProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const location = useLocation();
  const { user } = useAuth();

  // Routes that should have a different layout (like full-screen pages)
  const fullScreenRoutes = ['/login', '/register', '/forgot-password'];
  const isFullScreenRoute = fullScreenRoutes.includes(location.pathname);

  if (isFullScreenRoute) {
    return <div className="min-h-screen bg-gray-50 dark:bg-gray-900">{children}</div>;
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Sidebar - hidden by default on mobile */}
      <div
        className={`
          ${isMobile ? 'fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out' : ''}
          ${isMobile && !isSidebarOpen ? '-translate-x-full' : 'translate-x-0'}
        `}
      >
        <Sidebar isOpen={!isMobile || isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      </div>

      {/* Mobile sidebar overlay */}
      {isMobile && isSidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 z-40 transition-opacity duration-300 ease-in-out"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} />

        {/* Main scrollable container */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 dark:bg-gray-900 pb-16 md:pb-0">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Page container with max width and responsive padding */}
            <div className="max-w-7xl mx-auto">
              {children}
            </div>
          </div>
        </main>

        {/* Mobile navigation */}
        {isMobile && <MobileNav />}
      </div>
    </div>
  );
};

export default BaseLayout;